import React, { type ReactNode, type RefObject, useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { PopupProps } from '@atlaskit/popup/types';
import { applyInitialFocus } from '@atlaskit/top-layer/apply-initial-focus';

import { shouldMoveFocus } from './should-move-focus';

type FocusErrorExperienceProps = {
	children: ReactNode;
	role?: PopupProps['role'];
	shouldFocus: boolean;
	shouldTransferFocusRef: RefObject<boolean>;
	triggerRef: RefObject<HTMLElement | null>;
};

export function FocusErrorExperience({
	children,
	role,
	shouldFocus,
	shouldTransferFocusRef,
	triggerRef,
}: FocusErrorExperienceProps): React.JSX.Element {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (!shouldFocus || !fg('platform_dst-automated_focus')) {
			return;
		}

		const container = ref.current?.parentElement;

		if (!container || !shouldMoveFocus({ container, role, shouldTransferFocusRef, triggerRef })) {
			return;
		}

		const result = applyInitialFocus({ container, role });

		if (!result.applied) {
			applyInitialFocus({ container, initialFocusRef: ref, role });
		}
	}, [role, shouldFocus, shouldTransferFocusRef, triggerRef]);

	return (
		<span ref={ref} tabIndex={shouldFocus ? -1 : undefined}>
			{children}
		</span>
	);
}
